import React from "react";

interface PrimaryButtonProps {
  variant?: "primary" | "accent" | "light" | "outline" | "link";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  icon?: string;
  iconPosition?: "left" | "right";
  children: React.ReactNode;
  onClick?: () => void;
  type?: "button" | "submit";
  href?: string;
  target?: string;
  rel?: string;
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
}

const variantStyles: Record<
  string,
  { className: string; style?: React.CSSProperties }
> = {
  primary: {
    className: "btn text-white fw-semibold",
    style: { backgroundColor: "#0B2545" },
  },
  accent: {
    className: "btn text-white fw-semibold d-flex align-items-center gap-2",
    style: { backgroundColor: "#2A9D8F" },
  },
  light: {
    className: "btn btn-light fw-semibold",
  },
  outline: {
    className: "btn btn-outline-secondary fw-semibold",
  },
  link: {
    className: "btn btn-link text-decoration-none text-success fw-semibold p-0",
  },
};

const sizeClasses: Record<string, string> = {
  sm: "btn-sm",
  md: "",
  lg: "btn-lg",
};

export default function PrimaryButton({
  variant = "primary",
  size = "md",
  fullWidth = false,
  icon,
  iconPosition = "left",
  children,
  onClick,
  type = "button",
  href,
  target,
  rel,
  className = "",
  style,
  disabled = false,
}: PrimaryButtonProps) {
  const base = variantStyles[variant];
  const sizeClass = sizeClasses[size];
  const fullWidthClass = fullWidth ? "w-100" : "";

  const classes = [base.className, sizeClass, fullWidthClass, className]
    .filter(Boolean)
    .join(" ");

  const mergedStyle = { ...(base.style || {}), ...style };

  const content = (
    <>
      {icon && iconPosition === "left" && (
        <i className={`${icon} ${children ? "me-2" : ""}`}></i>
      )}
      {children}
      {icon && iconPosition === "right" && (
        <i className={`${icon} ${children ? "ms-2" : ""}`}></i>
      )}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={classes}
        style={mergedStyle}
        target={target}
        rel={rel}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={classes}
      style={mergedStyle}
      onClick={onClick}
      disabled={disabled}
    >
      {content}
    </button>
  );
}
